import { buildRemovedBatches } from "../RemovedDevices/buildRemovedBatches";
import type { RemovalGroup } from "../cableEditGeometry";
import type { RemovedDevice } from "../RemovedDevices";
import type { GapTranslate } from "../findCableGaps";
import type { KarlAsk, KarlAskFocus } from "../karlAsks";

export interface BuildRemovalAsksArgs {
  removed: RemovedDevice[];
  groups: RemovalGroup[];
  t: GapTranslate;
}

export type BuildRemovalAsks = (args: BuildRemovalAsksArgs) => KarlAsk[];

const line = (
  t: GapTranslate,
  key: string,
  params?: Record<string, string | number>,
): string => t(`smartSurveyor.cablesBeta.${key}`, params);

/**
 * Taking devices out is a question, not a subtraction — the same rule the
 * roster already follows for a struck board. Removing a group used to advance
 * the walk to the next run with the devices simply gone: *"there is no clue
 * what happened to these deleted one"*.
 *
 * Asked LAST, behind the whole route walk. It used to be first, on the argument
 * that a removal should land while he still remembers making it — a real cost,
 * and it lost: a removal outlives the session that made it, so the first thing
 * he met on opening the panel was a question about three devices he struck
 * yesterday, at "1 of 88, 0 answered", before he had walked a single run.
 * *"why chat starts with removed elements? i think we should let them right in
 * the end."* Nothing is lost by waiting — the devices stay parked in their own
 * group, counted nowhere, each one its own row in the removals list with its
 * own Put back and Move onto.
 */
const focusOf = (batch: {
  fromCircuits: string[];
  devices: { x?: number; y?: number }[];
}): KarlAskFocus | null => {
  const positions = batch.devices
    .filter((device) => device.x !== undefined && device.y !== undefined)
    .map((device) => ({ x: device.x as number, y: device.y as number }));
  const circuitId =
    batch.fromCircuits.length === 1 ? batch.fromCircuits[0] : undefined;
  if (!circuitId && !positions.length) return null;
  return {
    ...(circuitId ? { circuitId } : {}),
    ...(positions.length ? { positions } : {}),
  };
};

export const buildRemovalAsks: BuildRemovalAsks = ({ removed, groups, t }) => {
  const batches = buildRemovedBatches({ removed, groups });

  return batches
    .filter((batch) => batch.ordinal > 0)
    .map((batch) => {
      const from = batch.fromCircuits.join(", ");
      const focus = focusOf(batch);
      return {
        id: `removed:${batch.id}`,
        group: "routes" as const,
        question: line(t, "askRemovedWhere", {
          count: batch.devices.length,
          route: from,
        }),
        subject: from,
        subjectKey: batch.id,
        detail: line(t, "askRemovedDetail", { count: batch.devices.length }),
        // The runs they came off, so he can open the sheet they are drawn on
        // without leaving the question — the same reference a route ask gets.
        ...(batch.fromCircuits.length
          ? { diagramRefs: batch.fromCircuits }
          : {}),
        // KEEP HIS RUN ON THE PLAN. Every ask focuses something, and an ask
        // with no focus focuses NOTHING — so striking devices off the run he
        // had isolated dropped the isolation at the moment this question
        // arrived: *"when i deleted this right side devices i want to preserve
        // the current selected route to be active, so i see whats going on"*.
        // A removal off one run keeps that run up, framed on the devices it
        // is asking about. Off several runs there is no single answer, so the
        // devices alone are framed and the selection is left as it is.
        ...(focus ? { focus } : {}),
        answers: [
          {
            id: "pick",
            label: line(t, "askRemovedPick"),
            tone: "primary" as const,
            action: { kind: "pickRoute" as const, groupId: batch.id },
          },
          {
            id: "park",
            label: line(t, "askRemovedPark"),
            action: { kind: "parkRemovalGroup" as const, groupId: batch.id },
          },
          {
            id: "back",
            label: line(t, "askRemovedPutBack", { route: from }),
            action: { kind: "restoreRemovalGroup" as const, groupId: batch.id },
          },
        ],
      };
    });
};